// ─── Research Chart Engine ─────────────────────────────────────────────────────
// Generates publication-quality SVG charts from REAL computed data.
// Pure JavaScript SVG generation — no external dependencies.

const PALETTE = {
  blue: '#2563eb', red: '#dc2626', green: '#059669', amber: '#d97706',
  purple: '#7c3aed', slate: '#64748b', teal: '#0d9488', indigo: '#4f46e5',
}

const SEQUENTIAL = ['#1e3a5f', '#2563eb', '#60a5fa', '#bfdbfe', '#fecaca', '#ef4444', '#991b1b']

// ── SVG helpers ───────────────────────────────────────────────────────────────

function svgTag(tag, attrs, children = '') {
  const attrStr = Object.entries(attrs).map(([k, v]) => `${k}="${v}"`).join(' ')
  return `<${tag} ${attrStr}>${children}</${tag}>`
}

function svgElement(tag, attrs) {
  const attrStr = Object.entries(attrs).map(([k, v]) => `${k}="${v}"`).join(' ')
  return `<${tag} ${attrStr}/>`
}

function wrapSVG(width, height, content, title = '') {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" font-family="'Times New Roman', Georgia, serif">
  <rect width="${width}" height="${height}" fill="white"/>
  ${title ? `<text x="${width / 2}" y="20" text-anchor="middle" font-size="13" font-weight="bold" fill="#0f172a">${escapeXml(title)}</text>` : ''}
  ${content}
</svg>`
}

function escapeXml(str) {
  return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function linearScale(domain, range) {
  const [d0, d1] = domain, [r0, r1] = range
  return v => r0 + ((v - d0) / (d1 - d0 || 1)) * (r1 - r0)
}

function niceRange(vals, padding = 0.05) {
  const min = Math.min(...vals), max = Math.max(...vals)
  const span = max - min || 1
  return [min - span * padding, max + span * padding]
}

function ticks(min, max, count = 5) {
  const step = (max - min) / (count - 1)
  return Array.from({ length: count }, (_, i) => min + i * step)
}

function fmt(v, dec = 3) {
  if (v == null) return 'N/A'
  return Number(v.toFixed(dec)).toString()
}

// ── Scatter Plot with Regression Line ────────────────────────────────────────

export function scatterRegressionSVG({ xData, yData, xLabel, yLabel, title, beta, intercept, rSquared }) {
  const W = 560, H = 420
  const margin = { top: 40, right: 30, bottom: 60, left: 70 }
  const iW = W - margin.left - margin.right
  const iH = H - margin.top - margin.bottom

  const xRange = niceRange(xData)
  const yRange = niceRange(yData)

  const scaleX = linearScale(xRange, [0, iW])
  const scaleY = linearScale(yRange, [iH, 0])

  const xTicks = ticks(...xRange, 6)
  const yTicks = ticks(...yRange, 5)

  // Grid lines
  let grid = ''
  for (const t of yTicks) {
    grid += svgElement('line', { x1: 0, y1: scaleY(t), x2: iW, y2: scaleY(t), stroke: '#e2e8f0', 'stroke-width': 1 }) + '\n'
  }
  for (const t of xTicks) {
    grid += svgElement('line', { x1: scaleX(t), y1: 0, x2: scaleX(t), y2: iH, stroke: '#e2e8f0', 'stroke-width': 1 }) + '\n'
  }

  // Data points
  const points = xData.map((x, i) => {
    const cx = scaleX(x), cy = scaleY(yData[i])
    return svgElement('circle', { cx, cy, r: 4, fill: PALETTE.blue, opacity: 0.65 })
  }).join('\n')

  // Regression line
  let regLine = ''
  if (beta != null && intercept != null) {
    const x1 = xRange[0], x2 = xRange[1]
    const y1 = intercept + beta * x1
    const y2 = intercept + beta * x2
    regLine = svgElement('line', {
      x1: scaleX(x1), y1: scaleY(y1),
      x2: scaleX(x2), y2: scaleY(y2),
      stroke: PALETTE.red, 'stroke-width': 2, 'stroke-dasharray': '6,3',
    })
  }

  // R² annotation
  const annotation = rSquared != null
    ? svgTag('text', { x: iW - 5, y: 15, 'text-anchor': 'end', 'font-size': 11, fill: '#475569' }, `R² = ${fmt(rSquared, 3)}`)
    : ''

  // Axes
  const xAxis = `
    ${svgElement('line', { x1: 0, y1: iH, x2: iW, y2: iH, stroke: '#334155', 'stroke-width': 1.5 })}
    ${xTicks.map(t => `
      ${svgElement('line', { x1: scaleX(t), y1: iH, x2: scaleX(t), y2: iH + 5, stroke: '#334155', 'stroke-width': 1 })}
      ${svgTag('text', { x: scaleX(t), y: iH + 18, 'text-anchor': 'middle', 'font-size': 10, fill: '#475569' }, fmt(t, 1))}
    `).join('')}
    ${svgTag('text', { x: iW / 2, y: iH + 40, 'text-anchor': 'middle', 'font-size': 12, fill: '#334155' }, escapeXml(xLabel))}
  `
  const yAxis = `
    ${svgElement('line', { x1: 0, y1: 0, x2: 0, y2: iH, stroke: '#334155', 'stroke-width': 1.5 })}
    ${yTicks.map(t => `
      ${svgElement('line', { x1: -5, y1: scaleY(t), x2: 0, y2: scaleY(t), stroke: '#334155', 'stroke-width': 1 })}
      ${svgTag('text', { x: -10, y: scaleY(t) + 4, 'text-anchor': 'end', 'font-size': 10, fill: '#475569' }, fmt(t, 1))}
    `).join('')}
    ${svgTag('text', {
      x: -(iH / 2), y: -52, 'text-anchor': 'middle', 'font-size': 12, fill: '#334155',
      transform: `rotate(-90)`,
    }, escapeXml(yLabel))}
  `

  const inner = `<g transform="translate(${margin.left},${margin.top})">
    ${grid}${points}${regLine}${xAxis}${yAxis}${annotation}
  </g>`

  return wrapSVG(W, H, inner, title)
}

// ── Correlation Heatmap ───────────────────────────────────────────────────────

export function correlationHeatmapSVG({ matrix, pvalues, variables, title }) {
  const n = variables.length
  const cellSize = Math.min(70, Math.floor(460 / n))
  const labelW = 120
  const W = labelW + n * cellSize + 20
  const H = labelW + n * cellSize + 20
  const offsetX = labelW, offsetY = labelW

  // Color: red (-1) → white (0) → blue (+1)
  function corrColor(r) {
    if (r == null) return '#f1f5f9'
    const t = Math.min(1, Math.max(-1, r))
    if (t > 0) {
      const g = Math.round(255 * (1 - t * 0.7))
      return `rgb(${g},${g},255)`
    } else {
      const gb = Math.round(255 * (1 + t * 0.7))
      return `rgb(255,${gb},${gb})`
    }
  }

  function textColor(r) {
    return Math.abs(r || 0) > 0.4 ? 'white' : '#334155'
  }

  let cells = ''
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const r = matrix[variables[i]]?.[variables[j]]
      const p = pvalues[variables[i]]?.[variables[j]]
      const x = offsetX + j * cellSize
      const y = offsetY + i * cellSize
      const sig = p != null && p < 0.05
      cells += svgTag('rect', { x, y, width: cellSize, height: cellSize, fill: corrColor(r), stroke: '#fff', 'stroke-width': 1 }, '')
      if (r != null) {
        cells += svgTag('text', {
          x: x + cellSize / 2, y: y + cellSize / 2 - 3,
          'text-anchor': 'middle', 'dominant-baseline': 'middle',
          'font-size': Math.max(8, cellSize * 0.2), fill: textColor(r), 'font-weight': sig ? 'bold' : 'normal',
        }, fmt(r, 2))
        if (sig && i !== j) {
          cells += svgTag('text', {
            x: x + cellSize / 2, y: y + cellSize / 2 + 9,
            'text-anchor': 'middle', 'font-size': 9, fill: textColor(r),
          }, '*')
        }
      }
    }
  }

  // Variable labels on top
  const topLabels = variables.map((v, j) => {
    const x = offsetX + j * cellSize + cellSize / 2
    const y = offsetY - 8
    return svgTag('text', {
      x, y, 'text-anchor': 'end', 'font-size': Math.max(8, Math.min(11, cellSize * 0.18)),
      fill: '#334155', transform: `rotate(-45,${x},${y})`,
    }, escapeXml(v))
  }).join('')

  // Variable labels on left
  const leftLabels = variables.map((v, i) => {
    return svgTag('text', {
      x: offsetX - 8, y: offsetY + i * cellSize + cellSize / 2,
      'text-anchor': 'end', 'dominant-baseline': 'middle',
      'font-size': Math.max(8, Math.min(11, cellSize * 0.18)), fill: '#334155',
    }, escapeXml(v))
  }).join('')

  const legend = `<text x="${W - 15}" y="${H - 5}" text-anchor="end" font-size="9" fill="#94a3b8">* p &lt; .05</text>`

  return wrapSVG(W, H, cells + topLabels + leftLabels + legend, title || 'Correlation Matrix')
}

// ── Coefficient Forest Plot ───────────────────────────────────────────────────

export function coefficientPlotSVG({ coefficients, dvLabel, title }) {
  const entries = Object.entries(coefficients).filter(([k]) => k !== 'const')
  const n = entries.length
  const W = 560, rowH = 42
  const H = 60 + n * rowH + 40
  const marginLeft = 160, marginRight = 50

  const allCIs = entries.flatMap(([, c]) => [c.ci_lower, c.ci_upper, c.beta])
  const xRange = niceRange(allCIs, 0.08)
  const scaleX = linearScale(xRange, [marginLeft, W - marginRight])
  const zeroX = scaleX(0)

  // Zero line
  let inner = svgElement('line', { x1: zeroX, y1: 20, x2: zeroX, y2: H - 30, stroke: '#94a3b8', 'stroke-width': 1, 'stroke-dasharray': '4,3' })

  // Axis
  const xTicks = ticks(...xRange, 7)
  inner += xTicks.map(t => `
    ${svgElement('line', { x1: scaleX(t), y1: H - 30, x2: scaleX(t), y2: H - 25, stroke: '#475569', 'stroke-width': 1 })}
    ${svgTag('text', { x: scaleX(t), y: H - 14, 'text-anchor': 'middle', 'font-size': 9, fill: '#64748b' }, fmt(t, 2))}
  `).join('')
  inner += svgElement('line', { x1: marginLeft, y1: H - 30, x2: W - marginRight, y2: H - 30, stroke: '#334155', 'stroke-width': 1.5 })

  // Coefficient rows
  entries.forEach(([name, c], i) => {
    const y = 50 + i * rowH
    const color = c.significant ? PALETTE.blue : c.marginal ? PALETTE.amber : PALETTE.slate
    const cx = scaleX(c.beta)
    const ciL = scaleX(c.ci_lower), ciR = scaleX(c.ci_upper)

    // Variable name
    inner += svgTag('text', { x: marginLeft - 8, y: y + 6, 'text-anchor': 'end', 'font-size': 11, fill: '#334155' }, escapeXml(name))

    // CI whisker line
    inner += svgElement('line', { x1: ciL, y1: y, x2: ciR, y2: y, stroke: color, 'stroke-width': 2 })
    // End caps
    inner += svgElement('line', { x1: ciL, y1: y - 5, x2: ciL, y2: y + 5, stroke: color, 'stroke-width': 1.5 })
    inner += svgElement('line', { x1: ciR, y1: y - 5, x2: ciR, y2: y + 5, stroke: color, 'stroke-width': 1.5 })
    // Point estimate diamond
    inner += svgTag('polygon', {
      points: `${cx},${y - 7} ${cx + 7},${y} ${cx},${y + 7} ${cx - 7},${y}`,
      fill: color, stroke: 'white', 'stroke-width': 1,
    }, '')

    // β and p-value annotation
    const sig = c.significant ? '**' : c.marginal ? '*' : ''
    inner += svgTag('text', { x: W - marginRight + 5, y: y + 5, 'font-size': 10, fill: color }, `β=${fmt(c.beta, 3)}${sig}`)
    inner += svgTag('text', { x: W - marginRight + 5, y: y + 17, 'font-size': 9, fill: '#94a3b8' }, `p=${fmt(c.p_value, 3)}`)
  })

  // Legend
  inner += svgTag('text', { x: marginLeft, y: H - 4, 'font-size': 9, fill: '#94a3b8' }, '** p&lt;.05  * p&lt;.10  DV: ' + escapeXml(dvLabel || ''))

  return wrapSVG(W, H, inner, title || 'Regression Coefficients')
}

// ── Histogram ─────────────────────────────────────────────────────────────────

export function histogramSVG({ values, label, title, bins = 16 }) {
  const W = 500, H = 340
  const margin = { top: 35, right: 20, bottom: 55, left: 60 }
  const iW = W - margin.left - margin.right
  const iH = H - margin.top - margin.bottom

  if (!values || values.length < 3) return wrapSVG(W, H, '', title || '')

  const min = Math.min(...values), max = Math.max(...values)
  const binWidth = (max - min) / bins
  const counts = new Array(bins).fill(0)
  values.forEach(v => {
    const b = Math.min(bins - 1, Math.floor((v - min) / (binWidth || 1)))
    counts[b]++
  })

  const maxCount = Math.max(...counts)
  const scaleX = linearScale([min, max], [0, iW])
  const scaleY = linearScale([0, maxCount], [iH, 0])

  const bars = counts.map((c, i) => {
    const x = scaleX(min + i * binWidth)
    const w = Math.max(1, scaleX(min + (i + 1) * binWidth) - x - 1)
    const y = scaleY(c)
    const bH = iH - y
    return svgTag('rect', { x, y, width: w, height: Math.max(0, bH), fill: PALETTE.blue, opacity: 0.75, stroke: 'white', 'stroke-width': 0.5 }, '')
  }).join('')

  const xTicks = ticks(min, max, 6)
  const xAxis = xTicks.map(t => `
    ${svgElement('line', { x1: scaleX(t), y1: iH, x2: scaleX(t), y2: iH + 5, stroke: '#475569' })}
    ${svgTag('text', { x: scaleX(t), y: iH + 18, 'text-anchor': 'middle', 'font-size': 10, fill: '#475569' }, fmt(t, 1))}
  `).join('')

  const yTicks = ticks(0, maxCount, 5).map(Math.round)
  const yAxis = yTicks.map(t => `
    ${svgElement('line', { x1: -4, y1: scaleY(t), x2: 0, y2: scaleY(t), stroke: '#475569' })}
    ${svgTag('text', { x: -8, y: scaleY(t) + 4, 'text-anchor': 'end', 'font-size': 10, fill: '#475569' }, String(t))}
  `).join('')

  const inner = `<g transform="translate(${margin.left},${margin.top})">
    ${bars}
    ${svgElement('line', { x1: 0, y1: iH, x2: iW, y2: iH, stroke: '#334155', 'stroke-width': 1.5 })}
    ${svgElement('line', { x1: 0, y1: 0, x2: 0, y2: iH, stroke: '#334155', 'stroke-width': 1.5 })}
    ${xAxis}${yAxis}
    ${svgTag('text', { x: iW / 2, y: iH + 38, 'text-anchor': 'middle', 'font-size': 12, fill: '#334155' }, escapeXml(label))}
    ${svgTag('text', { x: -iH / 2, y: -45, 'text-anchor': 'middle', 'font-size': 12, fill: '#334155', transform: 'rotate(-90)' }, 'Frequency')}
  </g>`

  return wrapSVG(W, H, inner, title || `Distribution: ${label}`)
}

// ── Residuals vs Fitted Plot ──────────────────────────────────────────────────

export function residualsPlotSVG({ fitted, residuals, title }) {
  return scatterRegressionSVG({
    xData: fitted,
    yData: residuals,
    xLabel: 'Fitted Values',
    yLabel: 'Standardised Residuals',
    title: title || 'Residuals vs Fitted',
    beta: 0,
    intercept: 0,
    rSquared: null,
  })
}

// ── SVG download helper ───────────────────────────────────────────────────────

export function downloadSVG(svgString, filename) {
  const blob = new Blob([svgString], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  URL.revokeObjectURL(url)
}

export function svgToPngDataUrl(svgString) {
  return new Promise((resolve) => {
    const img = new Image()
    const blob = new Blob([svgString], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width * 2
      canvas.height = img.height * 2
      const ctx = canvas.getContext('2d')
      ctx.scale(2, 2)
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
    img.src = url
  })
}
